// Runtime configuration from /config.json (deploy-generated): API base URL and auth mode (dev or password), validated with Zod before the app renders.
import { createContext, use } from "react";
import { z } from "zod";

export const zAuthConfig = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("dev") }),
  z.object({ mode: z.literal("password") }),
]);

export const zRuntimeConfig = z.object({
  apiBaseUrl: z.string(),
  auth: zAuthConfig,
});

export type AuthConfig = z.infer<typeof zAuthConfig>;
export type RuntimeConfig = z.infer<typeof zRuntimeConfig>;

export class RuntimeConfigError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "RuntimeConfigError";
  }
}

export function parseRuntimeConfig(value: unknown): RuntimeConfig {
  const parsed = zRuntimeConfig.safeParse(value);
  if (!parsed.success) {
    const issues = parsed.error.issues.map(
      (issue) => `${issue.path.join(".") || "config"}: ${issue.message}`,
    );
    throw new RuntimeConfigError(`Invalid /config.json (${issues.join("; ")})`, { cause: parsed.error });
  }
  return parsed.data;
}

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  let response: Response;
  try {
    response = await fetchImpl("/config.json", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });
  } catch (error) {
    throw new RuntimeConfigError("Could not load /config.json.", { cause: error });
  }
  if (!response.ok) {
    throw new RuntimeConfigError(`Could not load /config.json (HTTP ${response.status}).`);
  }
  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    throw new RuntimeConfigError("/config.json is not valid JSON.", { cause: error });
  }
  return parseRuntimeConfig(body);
}

export const RuntimeConfigContext = createContext<RuntimeConfig | null>(null);

export function useRuntimeConfig(): RuntimeConfig {
  const config = use(RuntimeConfigContext);
  if (config === null) {
    throw new Error("useRuntimeConfig must be used inside RuntimeConfigContext.");
  }
  return config;
}
